import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { appDb } from "../config";
import { doc, getDoc } from "firebase/firestore";
import logo from "../assets/img/logo.png";
import { createClient } from "@supabase/supabase-js";

// Create a single supabase client for interacting with your database
const supabase = createClient(
  "https://pppxbumchydernriwxyu.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBwcHhidW1jaHlkZXJucml3eHl1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzAyOTgxMTIsImV4cCI6MjA0NTg3NDExMn0.5WiZCJJh8aolfx_TgJiu-z8Ok3qdh2lEjN4mLydMHJ4"
);

function Card() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plaqueData, setPlaqueData] = useState(null);
  const [cancelled, setCancelled] = useState(false);

  // Function to track events to Supabase
  const trackEvent = async (actionType) => {
    if (!id) return;
    
    try {
      const { error } = await supabase.from("plaques_events").insert({
        plaque_id: id,
        action_type: actionType,
        created_at: new Date().toISOString(),
      });
      
      if (error) {
        console.error("Error tracking event:", error);
      }
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  };

  useEffect(() => {
    // Add CSS animation for star pulse
    const styleId = "star-skeleton-animation";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes starPulse {
          0%, 100% {
            fill: #e5e7eb;
            stroke: #d1d5db;
          }
          20% {
            fill: #fbbf24;
            stroke: #f59e0b;
          }
          40% {
            fill: #e5e7eb;
            stroke: #d1d5db;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const lookupPlaque = async () => {
      if (!id) {
        navigate("/");
        return;
      }

      try {
        const plaqueDocRef = doc(appDb, "plaques", id);
        const plaqueDocSnap = await getDoc(plaqueDocRef);

        if (plaqueDocSnap.exists()) {
          const data = plaqueDocSnap.data();
          if (data.place_id) {
            setPlaqueData(data);
            
            // If consent is "auto", redirect immediately
            if (data.consent === "auto") {
              // Track review_started event before redirect
              await trackEvent("review_started");
              window.location.href = `https://search.google.com/local/writereview?placeid=${data.place_id}`;
            } else {
              // If consent is "ask" or not set, show consent screen
              setLoading(false);
              // Track consent_viewed event when consent screen is shown
              trackEvent("consent_viewed");
            }
          } else {
            setLoading(false);
            navigate("/");
          }
        } else {
          setLoading(false);
          navigate("/");
        }
      } catch (error) {
        console.error("Error looking up plaque:", error);
        setLoading(false);
        navigate("/");
      }
    };

    lookupPlaque();
  }, [id, navigate]);

  const handleGoToReview = async () => {
    if (plaqueData?.place_id) {
      // Track review_started event before redirect
      await trackEvent("review_started");
      window.location.href = `https://search.google.com/local/writereview?placeid=${plaqueData.place_id}`;
    }
  };

  const handleCancel = async () => {
    // Track cancelled event
    await trackEvent("cancelled");
    setCancelled(true);
  };

  const handleChangeMind = () => {
    setCancelled(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <StarSkeleton key={star} index={star} />
          ))}
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50 px-4">
        <div className="flex justify-center pt-4 mb-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Nami" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-gray-900">Nami reviews</span>
          </div>
        </div>
        <div className="flex items-center justify-center flex-1">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              That's a bummer, maybe next time :(
            </h4>
            <button
              onClick={handleChangeMind}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              I change my mind
            </button>
          </div>
        </div>
        <footer className="fixed bottom-0 left-0 right-0 flex md:items-center md:justify-between mb-0">
          <div className="bg-white w-full p-4">
            <span className="text-sm text-gray-500 sm:text-center dark:text-gray-400 ">
              © {new Date().getFullYear()}{" "}
              <a href="https://nami.africa" className="hover:underline">
                Nami
              </a>
              . All Rights Reserved.
            </span>
          </div>
        </footer>
      </div>
    );
  }

  if (plaqueData && plaqueData.consent === "ask") {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50 px-4">
        <div className="flex justify-center pt-4 mb-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Nami" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-gray-900">Nami reviews</span>
          </div>
        </div>
        <div className="flex items-center justify-center flex-1">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              This is a google review request
            </h4>
            <p className="text-gray-600 mb-6">
              This will redirect to a google review page for this business. Google
              authentication may be required as standard procedure. If you wish to
              proceed, click "Go to review page" below.
            </p>
            <button
              onClick={handleGoToReview}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-colors mb-4"
            >
              Go to review page
            </button>
            <div className="text-center">
              <button
                onClick={handleCancel}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        <footer className="fixed bottom-0 left-0 right-0 flex md:items-center md:justify-between mb-0">
          <div className="bg-white w-full p-4">
            <span className="text-sm text-gray-500 sm:text-center dark:text-gray-400 ">
              © {new Date().getFullYear()}{" "}
              <a href="https://nami.africa" className="hover:underline">
                Nami
              </a>
              . All Rights Reserved.
            </span>
          </div>
        </footer>
      </div>
    );
  }

  return null;
}

function StarSkeleton({ index }) {
  return (
    <svg
      className="w-8 h-8"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        style={{
          fill: "#e5e7eb",
          stroke: "#d1d5db",
          strokeWidth: "1",
          animation: `starPulse 2.5s ease-in-out infinite`,
          animationDelay: `${(index - 1) * 0.3}s`,
        }}
      />
    </svg>
  );
}

export default Card;
